import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IPermission,
    IPermissionScope,
    IRole,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    ModalService,
    PermissionService,
    RoleService,
} from '@services';
import UiValidators, { UI_NAME_MAX_LENGTH } from '@validators';

import difference from 'lodash-es/difference';
import intersection from 'lodash-es/intersection';
import keyBy from 'lodash-es/keyBy';
import uniqBy from 'lodash-es/uniqBy';
import {
    BehaviorSubject,
    Subject,
} from 'rxjs';
import {
    debounceTime,
    finalize,
    map,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

type CheckboxState = 'unchecked' | 'checked' | 'indeterminate';
type ActionType = 'View' | 'Edit' | 'Create' | 'Delete';

interface IPermissionGroup {
    localName: string;
    name: string;
    scope: IPermissionScope;
}
@Component({
    selector: 'ui-role-modal',
    templateUrl: './role.modal.html',
    styleUrls: ['role.modal.scss'],
    providers: [FormBuilder, RoleService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class RoleModalComponent
    extends UiModalComponent<IRole>
    implements OnInit, OnDestroy {

    @ViewChild('dialog', { static: false })
    public dialog!: ElementRef<HTMLElement>;

    @ViewChild(MatExpansionPanel, { static: false })
    public warning!: MatExpansionPanel;

    public readonly elevatedPermissions = ['Settings.Edit', 'Roles.Edit', 'Users.Edit', 'Robots.Edit', 'Machines.Edit'];
    public readonly actions: ActionType[] = ['View', 'Edit', 'Create', 'Delete'];
    public form?: FormGroup;
    public loading$ = new BehaviorSubject(false);

    public get isWarningVisible() {
        const checked = this.permissions$.value.filter(p => this._boxChecked(p.name));
        const folderPermissionsChecked = checked.filter(p => p.scope === 'Folder').length;
        const globalPermissionsChecked = checked.filter(p => p.scope === 'Global').length;
        return folderPermissionsChecked && globalPermissionsChecked;
    }

    public get shouldReviewWarnings() {
        return this.form && this.form.dirty && this.isWarningVisible && !this.didExpandWarning;
    }

    public didExpandWarning = false;
    public globalActionChecked = {} as Record<ActionType, CheckboxState>;
    public folderActionChecked = {} as Record<ActionType, CheckboxState>;
    public groupChecked: Record<string, CheckboxState> = {};

    public permissions$ = new BehaviorSubject<IPermission[]>([]);
    public permissionGroups$ = new BehaviorSubject<IPermissionGroup[]>([]);
    public globalPermissionGroups$ = this.permissionGroups$.pipe(
        map(groups => groups.filter(g => g.scope === 'Global')),
    );
    public folderPermissionGroups$ = this.permissionGroups$.pipe(
        map(groups => groups.filter(g => g.scope === 'Folder')),
    );

    private _roleService = this._injector.get<RoleService>(RoleService);
    private _permissionsService = this._injector.get<PermissionService>(PermissionService);
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);
    private _destroyed$ = new Subject();

    ngOnInit() {
        this.context.data.permissions = this.context.data.permissions || [];

        if (this.context.isNewEntry) {
            this.context.data.isEditable = true;
        }

        this._permissionsService.getPermissions()
            .pipe(
                map(permissions => {
                    const groups =
                        uniqBy(
                            permissions
                                .map(p => ({ ...p, name: p.name.split('.')[0] }))
                                .map(p => ({
                                    name: p.name,
                                    scope: p.scope,
                                    localName: `page_${p.name.toLowerCase()}`,
                                })),
                            g => g.name,
                        );

                    const groupsByName = keyBy(groups, g => g.name);
                    const enabledPermissions = new Set(permissions.map(p => p.name));
                    const disabledPermissions = new Set<string>();

                    groups.forEach(({ name: groupName }) => {
                        this.actions.forEach(action => {
                            const permName = groupName + '.' + action;
                            if (!enabledPermissions.has(permName)) {
                                permissions.push({
                                    isGranted: false,
                                    name: permName,
                                    scope: groupsByName[groupName].scope,
                                });
                                disabledPermissions.add(permName);
                            }
                        });
                    });

                    this.permissionGroups$.next(groups.sort((a, b) => a.localName.localeCompare(b.localName)));
                    this.permissions$.next(permissions);

                    return disabledPermissions;

                }),
                tap(this._registerForm),
                takeUntil(this._destroyed$),
            ).subscribe();
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public reviewWarning() {
        this.warning.open();
        this.dialog.nativeElement.scrollTop = 0;
    }

    public onSubmit() {
        const originalPerms = this.context.data.permissions.filter(p => p.isGranted).map(p => p.name);

        const newPerms = this.permissions$.value
            .filter(({ name }) => this._boxEnabled(name) && this._boxChecked(name))
            .map(p => p.name);

        const anyElevatedPermissions = intersection(difference(newPerms, originalPerms), this.elevatedPermissions).length > 0;

        if (anyElevatedPermissions) {
            this._modalService.onConfirm(null, () => this._submitData(), () => { },
                this._translateService.instant('role_modal_form_confirm_elevated_permissions'));
        } else {
            this._submitData();
        }
    }

    public toggleAction(action: ActionType, folderPermissions: boolean) {
        this._togglePermissions(p =>
            p.name.endsWith(action) &&
            (
                folderPermissions == null ||
                p.scope === 'Folder' === folderPermissions
            ),
        );
    }

    public togglePermissionGroup(permissionGroup: IPermissionGroup) {
        this._togglePermissions(p => p.name.startsWith(permissionGroup.name));
    }

    private _togglePermissions(predicate: (p: IPermission) => boolean) {
        const toToggle = this.permissions$.value.filter(predicate);
        const nextValue = toToggle.some(p => !this._boxChecked(p.name));
        toToggle.forEach(p => this.form!.get([p.name])!.setValue(nextValue));
        this.form!.markAsDirty();
    }

    private _registerForm = (disabledPermissions: Set<string>) => {
        const permissionGranted = keyBy(this.context.data.permissions.filter(p => p.isGranted), p => p.name);

        const controls = this.permissions$.value.reduce((ctrls, { name: permName }) => {
            ctrls[permName] = new FormControl({
                value: !!permissionGranted[permName],
                disabled: !this.context.data.isEditable || disabledPermissions.has(permName),
            });
            return ctrls;
        }, {} as Record<string, FormControl>);

        this.form = this._injector.get<FormBuilder>(FormBuilder).group({
            ...controls,
            'name': [
                this.context.data.name,
                Validators.compose([
                    UiValidators.required(),
                    Validators.maxLength(UI_NAME_MAX_LENGTH),
                ]),
            ],
        });

        this.form
            .valueChanges
            .pipe(
                startWith(null),
                debounceTime(50),
                switchMap(() => this.permissionGroups$),
                takeUntil(this._destroyed$),
            ).subscribe(groups => {
                groups.forEach(this._computePermissionGroupCheckboxState);
                this.actions.forEach(a => {
                    this._computeActionCheckboxState(a, true, groups);
                    this._computeActionCheckboxState(a, false, groups);
                });
                this._cd.detectChanges();
            });
    }

    private _computePermissionGroupCheckboxState = ({ name: group }: IPermissionGroup) => {
        const checked = this.permissions$.value
            .filter(({ name }) =>
                this._boxEnabled(name) &&
                this._boxChecked(name) &&
                name.startsWith(group),
            )
            .length;

        this.groupChecked[group] = this._computeCheckedState(checked, this.actions.length);
    }

    private _computeActionCheckboxState = (action: ActionType, folderPermissions: boolean, groups: IPermissionGroup[]) => {
        const checked = this.permissions$.value
            .filter(({ name, scope }) =>
                this._boxEnabled(name) &&
                this._boxChecked(name) &&
                name.endsWith(action) &&
                scope === 'Folder' === folderPermissions,
            )
            .length;

        const actionSet = folderPermissions ? this.folderActionChecked : this.globalActionChecked;
        const folderPermissionsCount = groups.filter(g => g.scope === 'Folder').length;
        const permissionCount = folderPermissions ? folderPermissionsCount
            : (groups.length - folderPermissionsCount);

        actionSet[action] = this._computeCheckedState(checked, permissionCount);
    }

    private _computeCheckedState(checked: number, total: number) {
        return checked === 0 ? 'unchecked'
            : checked === total ? 'checked'
                : 'indeterminate';
    }

    private _boxEnabled(key: string) {
        return this.form!.get([key])!.enabled;
    }

    private _boxChecked(key: string) {
        return this.form!.get([key])!.value;
    }

    private _submitData = () => {
        this.loading$.next(true);

        const payload: IRole = {
            id: this.context.data.id,
            name: this.form!.get('name')!.value,
            displayName: this.form!.get('name')!.value,
            permissions: this.permissions$.value
                .filter(p => this._boxEnabled(p.name))
                .map(p => ({ ...p, isGranted: this._boxChecked(p.name) })),
        };

        const req = this.context.isNewEntry
            ? this._roleService.add(payload)
            : this._roleService.update(this.context.data.id, payload);

        req.pipe(
            finalize(() => this.loading$.next(false)),
        ).subscribe(role => {
            this._dialogRef.close({
                type: this.context.isNewEntry ? ModalResultType.Add : ModalResultType.Update,
                data: this.context.isNewEntry ? role as IRole : this.context.data,
            });
        });
    }
}
